import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import type { FreeTable } from '@/features/checkin/types'
import { cn } from '@/lib/cn'
import { useApiErrorText } from '@/lib/staffGate'

import {
  usePlacementAction,
  usePlacementBoard,
  usePlacementFreeTables,
  usePlacementHistory,
} from './api'
import type { PlacementRow, PlacementStage } from './types'

/**
 * Le placement des tables : qui arrive, où il s'assoit, qui l'y a mis.
 *
 * Deux usages, un seul composant — deux écrans qui divergeraient finiraient
 * par se contredire sur la même table :
 *
 * * **`staff`** — l'onglet « Placement » de l'écran Salle, au téléphone de
 *   l'hôtesse. Trois blocs dans l'ordre du travail : les arrivés à installer
 *   (en tête, mis en avant, le numéro de table en grand), les attendus, les
 *   installés ;
 * * **`admin`** — la page du back-office, pour préparer la salle d'avance :
 *   filtres par étape, recherche par nom, tableau sur grand écran et cartes
 *   sur téléphone.
 *
 * L'arrivée n'est jamais saisie ici : elle vient des check-ins de la porte,
 * relus toutes les dix secondes.
 */
export function PlacementBoard({
  eventId,
  canUpdate,
  variant,
}: {
  eventId: string | undefined
  canUpdate: boolean
  variant: 'staff' | 'admin'
}) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const board = usePlacementBoard(eventId, true)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<'all' | PlacementStage>('all')

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return (board.data?.rows ?? []).filter((row) => {
      if (filter !== 'all' && row.stage !== filter) return false
      if (!needle) return true
      return [
        row.guest_name,
        row.reservation_name,
        row.order_number,
        row.table_number,
        row.table_name,
        ...(row.guests ?? []),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    })
  }, [board.data, query, filter])

  if (board.isPending) return <Skeleton className="mt-6 h-48 w-full" />
  if (board.isError) {
    return <p className="mt-6 text-sm text-state-danger">{errorText(board.error)}</p>
  }

  const counts = board.data.counts
  // Une étape par groupe : chaque filtre compte exactement ce qu'il montre.
  const byStage = (stage: PlacementStage) =>
    board.data.rows.filter((row) => row.stage === stage).length
  const filters: [typeof filter, string, number][] = [
    ['all', t('placement.filter.all'), counts.total],
    ['arrived', t('placement.filter.arrived'), byStage('arrived')],
    ['to_assign', t('placement.filter.toAssign'), byStage('to_assign')],
    ['assigned', t('placement.filter.assigned'), byStage('assigned')],
    ['seated', t('placement.filter.seated'), byStage('seated')],
  ]

  const search = (
    <input
      type="search"
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      placeholder={t('placement.search')}
      aria-label={t('placement.search')}
      className="h-11 w-full min-w-0 rounded-md border border-line bg-bg-elevated px-3 text-content-primary"
    />
  )

  if (variant === 'staff') {
    const waiting = rows.filter((row) => row.stage === 'arrived')
    const expected = rows.filter((row) => row.stage === 'to_assign' || row.stage === 'assigned')
    const seated = rows.filter((row) => row.stage === 'seated')
    return (
      <div className="mt-5 space-y-6">
        <div className="grid grid-cols-3 gap-2 text-center">
          <Counter label={t('placement.counts.waiting')} value={counts.waiting} strong={counts.waiting > 0} />
          <Counter label={t('placement.counts.expected')} value={counts.total - counts.waiting - counts.seated} />
          <Counter label={t('placement.counts.seated')} value={`${counts.seated} / ${counts.total}`} />
        </div>
        {search}
        {counts.total === 0 ? (
          <p className="mt-6 text-center text-content-muted">{t('placement.empty')}</p>
        ) : null}
        <Block title={t('placement.blocks.waiting')} rows={waiting} hideEmpty={false} emptyText={t('placement.blocks.noneWaiting')}>
          {(row) => <PlacementCard key={row.reservation_id} row={row} eventId={eventId} canUpdate={canUpdate} />}
        </Block>
        <Block title={t('placement.blocks.expected')} rows={expected}>
          {(row) => <PlacementCard key={row.reservation_id} row={row} eventId={eventId} canUpdate={canUpdate} />}
        </Block>
        <Block title={t('placement.blocks.seated')} rows={seated}>
          {(row) => <PlacementCard key={row.reservation_id} row={row} eventId={eventId} canUpdate={canUpdate} />}
        </Block>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {filters.map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={cn(
              'flex min-h-10 items-center gap-1.5 rounded-full border px-3 text-sm',
              filter === key
                ? 'border-ember-400 bg-ember-500/15 text-content-primary'
                : 'border-line text-content-secondary',
            )}
          >
            {label}
            <span className="font-mono text-caption text-content-muted">{count}</span>
          </button>
        ))}
      </div>
      <div className="max-w-md">{search}</div>

      {rows.length === 0 ? (
        <p className="py-8 text-center text-content-muted">
          {counts.total === 0 ? t('placement.empty') : t('placement.noMatch')}
        </p>
      ) : (
        <>
          {/* Téléphone et tablette : des cartes. Grand écran : un tableau. */}
          <ul className="grid gap-3 lg:hidden">
            {rows.map((row) => (
              <PlacementCard key={row.reservation_id} row={row} eventId={eventId} canUpdate={canUpdate} />
            ))}
          </ul>
          <div className="hidden lg:block">
            <PlacementTable rows={rows} eventId={eventId} canUpdate={canUpdate} />
          </div>
        </>
      )}
    </div>
  )
}

function Counter({ label, value, strong = false }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div
      className={cn(
        'min-w-0 rounded-lg border px-2 py-2',
        strong ? 'border-ember-400 bg-ember-500/10' : 'border-line-subtle',
      )}
    >
      <p className="font-display text-heading-md tabular-nums text-content-primary">{value}</p>
      <p className="text-caption leading-tight text-content-muted">{label}</p>
    </div>
  )
}

function Block({
  title,
  rows,
  children,
  hideEmpty = true,
  emptyText,
}: {
  title: string
  rows: PlacementRow[]
  children: (row: PlacementRow) => ReactNode
  hideEmpty?: boolean
  emptyText?: string
}) {
  if (rows.length === 0 && hideEmpty) return null
  return (
    <section>
      <p className="overline text-content-muted">
        {title} · {rows.length}
      </p>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-content-muted">{emptyText}</p>
      ) : (
        <ul className="mt-2 grid gap-3">{rows.map(children)}</ul>
      )}
    </section>
  )
}

function timeOf(iso: string | null, locale: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

/** « 1 / 2 arrivés », « Arrivés », « Attendus » — ce que la porte a vu. */
function ArrivalBadge({ row }: { row: PlacementRow }) {
  const { t } = useTranslation()
  if (row.stage === 'seated') {
    return <Badge tone="success">{t('placement.stage.seated')}</Badge>
  }
  if (row.arrival === 'partial') {
    return (
      <Badge tone="ember">
        {t('placement.arrival.partial', { arrived: row.arrived_count, expected: row.expected })}
      </Badge>
    )
  }
  if (row.arrival === 'arrived') {
    return (
      <Badge tone="ember">
        {row.expected > 0
          ? t('placement.arrival.all', { count: row.expected })
          : t('placement.arrival.arrived')}
      </Badge>
    )
  }
  return <Badge>{t('placement.arrival.expected')}</Badge>
}

function TableNumber({ row, size = 'lg' }: { row: PlacementRow; size?: 'lg' | 'md' }) {
  const { t } = useTranslation()
  if (!row.table_number) {
    return <span className="text-sm text-content-muted">{t('placement.noTable')}</span>
  }
  return (
    <span className="block min-w-0">
      <span
        className={cn(
          'block font-display leading-none text-champagne-300',
          size === 'lg' ? 'text-display-md' : 'text-heading-lg',
        )}
      >
        {row.table_number}
      </span>
      {row.table_name ? (
        <span className="mt-1 block truncate text-caption text-content-secondary">{row.table_name}</span>
      ) : null}
    </span>
  )
}

/** La carte d'un groupe : tout ce qu'il faut pour l'accompagner, à bout de bras. */
function PlacementCard({
  row,
  eventId,
  canUpdate,
}: {
  row: PlacementRow
  eventId: string | undefined
  canUpdate: boolean
}) {
  const { t, i18n } = useTranslation()
  const waiting = row.stage === 'arrived'
  return (
    <li
      className={cn(
        'rounded-lg border p-4',
        waiting ? 'border-ember-400 bg-ember-500/10' : 'border-line-subtle bg-bg-elevated',
        row.stage === 'seated' && 'opacity-90',
      )}
    >
      <div className="flex items-start gap-4">
        <div className="w-20 shrink-0 text-center">
          <TableNumber row={row} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="break-words font-display text-heading-sm text-content-primary">{row.guest_name}</p>
          <p className="mt-0.5 break-words text-sm text-content-secondary">
            {row.table_type_label}
            {row.package_label ? ` · ${row.package_label}` : ''}
            {' · '}
            {t('placement.people', { count: row.party_size })}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ArrivalBadge row={row} />
            {row.table_matches_type === false ? (
              <Badge tone="champagne">{t('placement.otherType')}</Badge>
            ) : null}
            {/* Acompte seulement : la table est confirmée, le solde reste à
                encaisser — à l'arrivée, le plus souvent. */}
            {row.balance_due ? <Badge tone="champagne">{t('placement.balanceDue')}</Badge> : null}
          </div>
          <RowFacts row={row} locale={i18n.language} />
        </div>
      </div>
      <PlacementActions row={row} eventId={eventId} canUpdate={canUpdate} />
    </li>
  )
}

function RowFacts({ row, locale }: { row: PlacementRow; locale: string }) {
  const { t } = useTranslation()
  return (
    <>
      {row.stage === 'arrived' && row.arrived_at ? (
        <p className="mt-2 text-sm text-ember-300">
          {t('placement.arrivedSince', {
            time: timeOf(row.arrived_at, locale),
            count: row.waiting_minutes ?? 0,
          })}
        </p>
      ) : null}
      {row.stage === 'seated' && row.seated_at ? (
        <p className="mt-2 text-sm text-content-secondary">
          {t('placement.seatedBy', {
            time: timeOf(row.seated_at, locale),
            name: row.seated_by_name ?? '—',
          })}
        </p>
      ) : null}
      {row.notes ? (
        <p className="mt-1 break-words text-caption text-content-muted">{row.notes}</p>
      ) : null}
      <TableContents row={row} />
    </>
  )
}

/** Le nom du groupe, ses prénoms, ses bouteilles : de quoi le reconnaître et
 *  savoir ce qui l'attend à table, sans ouvrir la réservation. */
function TableContents({ row }: { row: PlacementRow }) {
  const { t } = useTranslation()
  return (
    <>
      {row.reservation_name ? (
        <p className="mt-1 break-words text-sm text-champagne-300">{row.reservation_name}</p>
      ) : null}
      {row.guests && row.guests.length > 0 ? (
        <p className="mt-1 break-words text-caption text-content-secondary">
          {t('placement.guests')} : {row.guests.join(' · ')}
        </p>
      ) : null}
      {row.composition && row.composition.length > 0 ? (
        <p className="mt-1 break-words text-caption text-content-secondary">
          {t('placement.bottles')} : {row.composition.join(' · ')}
        </p>
      ) : null}
    </>
  )
}

type Mode = null | 'seat' | 'pick' | 'unseat' | 'history'

/**
 * Les gestes de l'hôtesse, sous la carte : installer (avec confirmation),
 * changer de table (motif si le groupe est assis), annuler l'installation
 * (motif obligatoire). Un seul panneau ouvert à la fois.
 */
function PlacementActions({
  row,
  eventId,
  canUpdate,
}: {
  row: PlacementRow
  eventId: string | undefined
  canUpdate: boolean
}) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const action = usePlacementAction(eventId)
  const [mode, setMode] = useState<Mode>(null)
  const [reason, setReason] = useState('')
  const seated = row.stage === 'seated'

  const open = (next: Mode) => {
    action.reset()
    setReason('')
    setMode(mode === next ? null : next)
  }
  const done = { onSuccess: () => setMode(null) }

  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        {canUpdate && !seated && row.table_id ? (
          <Button size="sm" variant={mode === 'seat' ? 'outline' : 'ember'} onClick={() => open('seat')}>
            {t('placement.actions.seat')}
          </Button>
        ) : null}
        {canUpdate ? (
          <Button size="sm" variant="outline" onClick={() => open('pick')}>
            {row.table_id ? t('placement.actions.change') : t('placement.actions.assign')}
          </Button>
        ) : null}
        {canUpdate && seated ? (
          <Button size="sm" variant="ghost" onClick={() => open('unseat')}>
            {t('placement.actions.unseat')}
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => open('history')}>
          {t('placement.actions.history')}
        </Button>
      </div>

      {action.error ? (
        <p className="mt-2 text-sm text-state-danger">{errorText(action.error)}</p>
      ) : null}

      {mode === 'seat' ? (
        <Panel>
          <p className="text-sm text-content-primary">
            {t('placement.confirmSeat', { table: row.table_number ?? '', name: row.guest_name })}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={action.isPending}
              onClick={() => action.mutate({ kind: 'seat', reservationId: row.reservation_id }, done)}
            >
              {t('placement.actions.confirmSeat')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
              {t('placement.actions.back')}
            </Button>
          </div>
        </Panel>
      ) : null}

      {mode === 'pick' ? (
        <Panel>
          {seated ? (
            <ReasonField
              label={t('placement.moveReason')}
              value={reason}
              onChange={setReason}
            />
          ) : null}
          <TableChoices
            row={row}
            eventId={eventId}
            disabled={action.isPending || (seated && reason.trim().length < 3)}
            onPick={(table) =>
              action.mutate(
                {
                  kind: 'table',
                  reservationId: row.reservation_id,
                  physical_table_id: table.id,
                  reason: seated ? reason.trim() : null,
                },
                done,
              )
            }
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {row.table_id && !seated ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={action.isPending}
                onClick={() =>
                  action.mutate(
                    { kind: 'table', reservationId: row.reservation_id, physical_table_id: null },
                    done,
                  )
                }
              >
                {t('placement.actions.release')}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
              {t('placement.actions.back')}
            </Button>
          </div>
        </Panel>
      ) : null}

      {mode === 'unseat' ? (
        <Panel>
          <ReasonField label={t('placement.unseatReason')} value={reason} onChange={setReason} />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={action.isPending || reason.trim().length < 3}
              onClick={() =>
                action.mutate(
                  { kind: 'unseat', reservationId: row.reservation_id, reason: reason.trim() },
                  done,
                )
              }
            >
              {t('placement.actions.confirmUnseat')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
              {t('placement.actions.back')}
            </Button>
          </div>
        </Panel>
      ) : null}

      {mode === 'history' ? (
        <Panel>
          <History reservationId={row.reservation_id} eventId={eventId} />
        </Panel>
      ) : null}
    </div>
  )
}

function Panel({ children }: { children: ReactNode }) {
  return <div className="mt-3 rounded-md border border-line-subtle bg-bg-base/40 p-3">{children}</div>
}

function ReasonField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className="mb-3 block text-caption text-content-muted">
      {label}
      <input
        value={value}
        maxLength={300}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 h-11 w-full min-w-0 rounded-md border border-line bg-bg-elevated px-3 text-sm text-content-primary"
      />
    </label>
  )
}

/** Les tables libres, le bon type d'abord — la même liste qu'à la porte. */
function TableChoices({
  row,
  eventId,
  disabled,
  onPick,
}: {
  row: PlacementRow
  eventId: string | undefined
  disabled: boolean
  onPick: (table: FreeTable) => void
}) {
  const { t } = useTranslation()
  const free = usePlacementFreeTables(row.reservation_id, eventId)
  const tables = (free.data ?? []).filter((table) => table.id !== row.table_id)
  const matching = tables.filter((table) => table.matches_type)
  const others = tables.filter((table) => !table.matches_type)

  if (free.isPending) return <p className="text-sm text-content-muted">{t('state.loading')}</p>
  if (tables.length === 0) {
    return <p className="text-sm text-state-warning">{t('placement.noFreeTable')}</p>
  }
  const grid = (list: FreeTable[]) => (
    <ul className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
      {list.map((table) => (
        <li key={table.id} className="min-w-0">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onPick(table)}
            className="w-full min-w-0 rounded-lg border border-line px-2 py-2 text-center hover:border-champagne-400 disabled:opacity-50"
          >
            <span className="block truncate font-display text-heading-md text-content-primary">
              {table.table_number}
            </span>
            {table.table_name ? (
              <span className="block truncate text-caption text-content-secondary">{table.table_name}</span>
            ) : null}
            {/* Jamais tronqué : « 4 Plätze » coupé en « 4 Plät… » ne se lit plus. */}
            <span className="block break-words text-xs leading-tight text-content-muted">
              {t('placement.seats', { count: table.capacity })}
            </span>
            {table.zone ? (
              <span className="block break-words text-xs leading-tight text-content-muted">
                {table.zone}
              </span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  )
  return (
    <div>
      {matching.length > 0 ? (
        <>
          <p className="text-caption text-content-muted">{t('placement.sameType')}</p>
          {grid(matching)}
        </>
      ) : null}
      {others.length > 0 ? (
        <>
          <p className="mt-3 text-caption text-content-muted">{t('placement.otherTypes')}</p>
          {grid(others)}
        </>
      ) : null}
    </div>
  )
}

const HISTORY_KEYS: Record<string, string> = {
  'tables.reservation.assign': 'door',
  'tables.placement.assign': 'assign',
  'tables.placement.seat': 'seat',
  'tables.placement.unseat': 'unseat',
}

function History({ reservationId, eventId }: { reservationId: string; eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const history = usePlacementHistory(reservationId, eventId)
  if (history.isPending) return <p className="text-sm text-content-muted">{t('state.loading')}</p>
  const entries = history.data ?? []
  if (entries.length === 0) {
    return <p className="text-sm text-content-muted">{t('placement.history.none')}</p>
  }
  return (
    <ol className="space-y-2">
      {entries.map((entry, index) => {
        const from = (entry.before?.table_number as string | null | undefined) ?? null
        const to = (entry.after?.table_number as string | null | undefined) ?? null
        return (
          <li key={`${entry.at}-${index}`} className="text-sm">
            <span className="tabular-nums text-content-muted">{timeOf(entry.at, i18n.language)}</span>{' '}
            <span className="text-content-primary">
              {t(`placement.history.${HISTORY_KEYS[entry.action] ?? 'assign'}`, {
                name: entry.actor,
                from: from ?? '—',
                to: to ?? '—',
              })}
            </span>
            {entry.reason ? (
              <span className="block break-words text-caption text-content-muted">
                {t('placement.history.reason', { reason: entry.reason })}
              </span>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

/** Le tableau du back-office, sur grand écran : une ligne par groupe. */
function PlacementTable({
  rows,
  eventId,
  canUpdate,
}: {
  rows: PlacementRow[]
  eventId: string | undefined
  canUpdate: boolean
}) {
  const { t, i18n } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(null)
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full table-fixed text-left text-sm">
        <thead className="border-b border-line-subtle text-caption uppercase text-content-muted">
          <tr>
            <th className="w-[24%] px-4 py-3">{t('placement.columns.guest')}</th>
            <th className="w-[20%] px-4 py-3">{t('placement.columns.type')}</th>
            <th className="w-[8%] px-4 py-3">{t('placement.columns.people')}</th>
            <th className="w-[12%] px-4 py-3">{t('placement.columns.table')}</th>
            <th className="w-[16%] px-4 py-3">{t('placement.columns.arrival')}</th>
            <th className="w-[20%] px-4 py-3">{t('placement.columns.seated')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <PlacementTableRow
              key={row.reservation_id}
              row={row}
              open={openId === row.reservation_id}
              onToggle={() => setOpenId(openId === row.reservation_id ? null : row.reservation_id)}
              eventId={eventId}
              canUpdate={canUpdate}
              locale={i18n.language}
            />
          ))}
        </tbody>
      </table>
    </Card>
  )
}

function PlacementTableRow({
  row,
  open,
  onToggle,
  eventId,
  canUpdate,
  locale,
}: {
  row: PlacementRow
  open: boolean
  onToggle: () => void
  eventId: string | undefined
  canUpdate: boolean
  locale: string
}) {
  const { t } = useTranslation()
  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-b border-line-subtle align-top hover:bg-bg-overlay/40',
          row.stage === 'arrived' && 'bg-ember-500/10',
        )}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <button type="button" className="break-words text-left text-content-primary" aria-expanded={open}>
            {row.guest_name}
          </button>
          {row.order_number ? (
            <span className="block font-mono text-caption text-content-muted">{row.order_number}</span>
          ) : null}
          <TableContents row={row} />
        </td>
        <td className="break-words px-4 py-3 text-content-secondary">
          {row.table_type_label}
          {row.package_label ? <span className="block text-caption">{row.package_label}</span> : null}
        </td>
        <td className="px-4 py-3 tabular-nums">{row.party_size}</td>
        <td className="px-4 py-3">
          <TableNumber row={row} size="md" />
          {row.table_matches_type === false ? (
            <span className="block text-caption text-champagne-300">{t('placement.otherType')}</span>
          ) : null}
        </td>
        <td className="px-4 py-3">
          <ArrivalBadge row={row} />
          {row.stage === 'arrived' && row.waiting_minutes !== null ? (
            <span className="mt-1 block text-caption text-ember-300">
              {t('placement.waitingFor', { count: row.waiting_minutes })}
            </span>
          ) : null}
        </td>
        <td className="px-4 py-3 text-content-secondary">
          {row.seated_at ? (
            <>
              <span className="tabular-nums">{timeOf(row.seated_at, locale)}</span>
              <span className="block break-words text-caption">{row.seated_by_name ?? '—'}</span>
            </>
          ) : (
            '—'
          )}
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-line-subtle">
          <td colSpan={6} className="px-4 pb-4">
            <PlacementActions row={row} eventId={eventId} canUpdate={canUpdate} />
          </td>
        </tr>
      ) : null}
    </>
  )
}
